import { createContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrandsWidget } from './Brands/BrandsWidget';
import { ArticlesWidget } from './ArticlesList/ArticlesWidget';
import { ArticlesCountWidget } from './ArticlesCount/ArticlesCountWidget';
import { SamplesWidget } from './Samples/SamplesWidget';
import { InquiriesWidget } from './Inquiries/InquiriesWidget';
import { MyShipmentsWidget } from './MyShipments/MyShipmentsWidget';
import { ProductApprovalsCountWidget } from './ProductApprovals/ProductApprovalsCountWidget';
import { ProductApprovalLeadTimeWidget } from './ProductApprovalLeadTime/ProductApprovalLeadTimeWidget';
import { ProductApprovalRatioWidget } from './ProductApprovalRatio/ProductApprovalRatioWidget';
import { ArticleProgressWidget } from './ArticleProgress/ArticleProgressWidget';
import { PaLeadTimeDistributionWidget } from './PaLeadTimeDistribution/PaLeadTimeDistributionWidget';
import { SupplierCoverageHeatmapWidget } from './SupplierCoverageHeatmap/SupplierCoverageHeatmapWidget';
import { PaRejectionReasonsWidget } from './PaRejectionReasons/PaRejectionReasonsWidget';
import { SpacerWidget } from './Spacer/SpacerWidget';
import { OffersWidget } from './Offers/OffersWidget';
import { Box } from '@chakra-ui/react';
import { useWidgetFactory } from './useWidgetFactory';
import {
  GridLayoutProps,
  WidgetsLayout,
} from './shared/components/WidgetsContainer';
import {
  ArticlesCountOptions,
  ArticleProgressOptions,
  InquiriesOptions,
  PaCountOptions,
  PaLeadTimeDistributionOptions,
  PaRejectionReasonsOptions,
  ProductApprovalLeadTimeOptions,
  ProductApprovalRatioOptions,
  SamplesOptions,
  SearchArticleOptions,
  SpacerWidgetOptions,
  WidgetOptions,
  WidgetType,
} from './shared/types';
import { getWidgetConfigs, UsedOnBoard, widgetBoardMap } from './widgets';
import { pulseWithoutScaleAnimation } from '@texas/resources/animations/animations';
import { widgetSizeConstraints } from './defaultWidgetLayouts';

const drawerCloseDurationMs = 200;
const scrollSettleDurationMs = 600;
const pulseDurationMs = 3000;
const pulseCycleDurationMs = 1000;

export type WidgetOption = {
  [_ in WidgetType]?: WidgetOptions;
};

export interface Widget {
  id: string;
  type: WidgetType;
  layoutIndex: string;
  name: string;
  categoryColor: string | null;
  widgetOptions?: WidgetOptions;
}

export interface WidgetLayout {
  layout: ReactGridLayout.Layouts;
  widgets: Widget[];
  currentLayout?: ReactGridLayout.Layout[];
}

export interface WidgetLayoutProps {
  defaultLayout: WidgetLayout;
  localStorageKey?: string;
  widgetOptions: WidgetOption;
  gridProps?: GridLayoutProps;
  board?: UsedOnBoard;
}

export const WidgetFactoryContext = createContext<ReturnType<
  typeof useWidgetFactory
> | null>(null);

export const useWidgetLayout = (props: WidgetLayoutProps) => {
  const { t } = useTranslation();
  const widgetConfigs = useMemo(() => getWidgetConfigs(t), [t]);
  const filteredLayout = useMemo(() => {
    if (!props.board) return props.defaultLayout;
    const board = props.board;
    const filteredWidgets = props.defaultLayout.widgets.filter((w) => {
      const boards = widgetBoardMap[w.type];
      return boards.length === 0 || boards.includes(board);
    });
    const allowedIds = new Set(filteredWidgets.map((w) => w.layoutIndex));
    return {
      ...props.defaultLayout,
      widgets: filteredWidgets,
      layout: Object.fromEntries(
        Object.entries(props.defaultLayout.layout).map(([bp, items]) => [
          bp,
          items.filter((item) => allowedIds.has(item.i)),
        ]),
      ),
    };
  }, [props.board, props.defaultLayout]);

  const widgetFactory = useWidgetFactory({
    defaultLayout: filteredLayout,
    widgetOptions: props.widgetOptions,
    localStorageKey: props.localStorageKey,
  });

  const {
    layout,
    setLayout,
    addNewWidget,
    recentlyAddedWidgetId,
    clearRecentlyAddedWidget,
  } = widgetFactory;
  const [pulsingWidgetId, setPulsingWidgetId] = useState<string | null>(null);

  const constrainedLayout = useMemo(() => {
    const widgetTypes = new Map(
      layout.widgets.map((widget) => [widget.layoutIndex, widget.type]),
    );

    return Object.fromEntries(
      Object.entries(layout.layout).map(([breakpoint, items]) => [
        breakpoint,
        items.map((item) => {
          const widgetType = widgetTypes.get(item.i);
          if (!widgetType) return item;

          const constraints = widgetSizeConstraints[widgetType];
          return {
            ...item,
            ...constraints,
            w: Math.max(item.w, constraints.minW),
            h: Math.min(Math.max(item.h, constraints.minH), constraints.maxH),
          };
        }),
      ]),
    );
  }, [layout.layout, layout.widgets]);

  useEffect(() => {
    if (!recentlyAddedWidgetId) return;

    const widgetElement = document.querySelector<HTMLElement>(
      `[data-widget-id="${recentlyAddedWidgetId}"]`,
    );
    if (!widgetElement) return;

    const scrollTimer = window.setTimeout(() => {
      widgetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    }, drawerCloseDurationMs);

    const pulseTimer = window.setTimeout(() => {
      setPulsingWidgetId(recentlyAddedWidgetId);
    }, drawerCloseDurationMs + scrollSettleDurationMs);
    const clearTimer = window.setTimeout(() => {
      setPulsingWidgetId(null);
      clearRecentlyAddedWidget(recentlyAddedWidgetId);
    }, drawerCloseDurationMs + scrollSettleDurationMs + pulseDurationMs);

    return () => {
      window.clearTimeout(scrollTimer);
      window.clearTimeout(pulseTimer);
      window.clearTimeout(clearTimer);
    };
  }, [clearRecentlyAddedWidget, recentlyAddedWidgetId]);

  const widgets = useMemo(() => {
    return (
      <WidgetFactoryContext.Provider value={widgetFactory}>
        <WidgetsLayout
          onLayoutChange={onLayoutChange}
          layout={constrainedLayout}
          gridProps={props.gridProps}
        >
          {layout.widgets.map((w) => {
            const name =
              w.name === w.type ? widgetConfigs[w.type].label : w.name;
            return (
              <Box
                key={w.layoutIndex}
                data-widget-id={w.layoutIndex}
                animation={
                  pulsingWidgetId === w.layoutIndex
                    ? pulseWithoutScaleAnimation(
                        '#f2902e',
                        pulseCycleDurationMs,
                      )
                    : undefined
                }
                zIndex={pulsingWidgetId === w.layoutIndex ? 1 : undefined}
              >
                {widgetComponent(
                  w.type,
                  w.id,
                  w.categoryColor,
                  name,
                  w.widgetOptions ?? props.widgetOptions[w.type],
                )}
              </Box>
            );
          })}
        </WidgetsLayout>
      </WidgetFactoryContext.Provider>
    );

    function onLayoutChange(
      currentLayout: ReactGridLayout.Layout[],
      allLayouts: ReactGridLayout.Layouts,
    ) {
      setLayout((s) => ({
        widgets: s.widgets,
        layout: allLayouts,
        currentLayout: currentLayout,
      }));
    }
  }, [
    widgetFactory,
    constrainedLayout,
    layout.widgets,
    pulsingWidgetId,
    props.gridProps,
    props.widgetOptions,
    setLayout,
    widgetConfigs,
  ]);

  return {
    widgetComponents: widgets,
    addNewWidget,
    widgets: layout.widgets,
    layout,
    setLayout,
  };
};

function widgetComponent(
  widgetType: WidgetType,
  id: string,
  customColor: string | null,
  name?: string,
  options?: WidgetOptions,
) {
  switch (widgetType) {
    case WidgetType.Brands:
      return (
        <BrandsWidget
          widgetId={id}
          options={options?.type === widgetType ? options : undefined}
          color={customColor}
          customTitle={options?.customWidgetTitle ?? name}
        />
      );
    case WidgetType.Articles: {
      const o = options as SearchArticleOptions | undefined;
      if (!o) return null;
      return (
        <ArticlesWidget
          color={customColor}
          widgetId={id}
          customTitle={o.customWidgetTitle ?? name}
          options={o}
        />
      );
    }
    case WidgetType.ArticlesCount: {
      const o = options as ArticlesCountOptions | undefined;
      const preConfigTitle = o?.preConfiguredWidget?.[id]?.widgetTitle;
      const resolvedOptions = (o?.preConfiguredWidget?.[id] ?? o) as
        | ArticlesCountOptions
        | undefined;
      if (!resolvedOptions) return null;
      return (
        <ArticlesCountWidget
          color={customColor}
          widgetId={id}
          customTitle={preConfigTitle ?? o?.customWidgetTitle ?? name}
          options={resolvedOptions}
        />
      );
    }
    case WidgetType.TrackSamples: {
      const o = options as SamplesOptions | undefined;
      return (
        <SamplesWidget
          widgetId={id}
          options={o}
          color={customColor}
          customTitle={o?.customWidgetTitle ?? name}
        />
      );
    }
    case WidgetType.TrackInquiries: {
      const opts = options as InquiriesOptions | undefined;
      return (
        <InquiriesWidget
          widgetId={id}
          color={customColor}
          customTitle={opts?.customWidgetTitle ?? name}
          options={opts}
        />
      );
    }
    case WidgetType.MyActiveShipments: {
      return (
        <MyShipmentsWidget
          widgetId={id}
          options={undefined}
          color={customColor}
          customTitle={options?.customWidgetTitle ?? name}
        />
      );
    }
    case WidgetType.ProductApprovalsCount: {
      const o = options as PaCountOptions | undefined;
      const preConfigTitle = o?.preConfiguredWidget?.[id]?.widgetTitle;
      const resolvedOptions = (o?.preConfiguredWidget?.[id] ?? o) as
        | PaCountOptions
        | undefined;
      if (!resolvedOptions) return null;
      return (
        <ProductApprovalsCountWidget
          color={customColor}
          widgetId={id}
          customTitle={preConfigTitle ?? o?.customWidgetTitle ?? name}
          options={resolvedOptions}
        />
      );
    }
    case WidgetType.ProductApprovalLeadTime: {
      const o = options as ProductApprovalLeadTimeOptions | undefined;
      return (
        <ProductApprovalLeadTimeWidget
          widgetId={id}
          color={customColor}
          customTitle={o?.customWidgetTitle ?? name}
          options={o}
        />
      );
    }
    case WidgetType.ProductApprovalRatio: {
      const o = options as ProductApprovalRatioOptions | undefined;
      return (
        <ProductApprovalRatioWidget
          widgetId={id}
          color={customColor}
          customTitle={o?.customWidgetTitle ?? name}
          options={o}
        />
      );
    }
    case WidgetType.ArticleProgress: {
      const o = options as ArticleProgressOptions | undefined;
      return (
        <ArticleProgressWidget
          widgetId={id}
          color={customColor}
          customTitle={o?.customWidgetTitle ?? name}
          options={o}
        />
      );
    }
    case WidgetType.PaLeadTimeDistribution: {
      const o = options as PaLeadTimeDistributionOptions | undefined;
      return (
        <PaLeadTimeDistributionWidget
          widgetId={id}
          color={customColor}
          customTitle={o?.customWidgetTitle ?? name}
          options={o}
        />
      );
    }
    case WidgetType.SupplierCoverageHeatmap: {
      return (
        <SupplierCoverageHeatmapWidget
          widgetId={id}
          color={customColor}
          customTitle={options?.customWidgetTitle ?? name}
        />
      );
    }
    case WidgetType.PaRejectionReasons: {
      const o = options as PaRejectionReasonsOptions | undefined;
      return (
        <PaRejectionReasonsWidget
          widgetId={id}
          color={customColor}
          customTitle={o?.customWidgetTitle ?? name}
          options={o}
        />
      );
    }
    case WidgetType.Spacer: {
      const o = options as SpacerWidgetOptions | undefined;
      return <SpacerWidget widgetId={id} options={o} />;
    }
    case WidgetType.TrackOffers: {
      return (
        <OffersWidget
          widgetId={id}
          color={customColor}
          customTitle={options?.customWidgetTitle ?? name}
        />
      );
    }
  }
}
